import React, { Dispatch, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { debounce } from '@mui/material/utils'
import InfoIcon from '@mui/icons-material/Info'
import { IssueEntity } from 'types/IssueEntity'
import { simplifyFraction } from '../utils'
import * as sharesOrUnits from '../../../utils/issues/sharesOrUnits.js'
import { FactsTableRow } from '../../components/FactsTable'
import { PlainNumericField, PlainNumericFieldProps } from '../../../components/fields/NumericField'
import { formatMoney, formatNumber } from '../../../packages/utils/numberFormatters'
import { DynamicIconButton } from '../../../utils/tooltip/DynamicIconButton'
import { UserMe } from '../../../api/UserApi'
import { SetStateAction } from 'jotai'
import { UserMeContext } from '../../../contexts/UserMeContext'

const Wrapper = ({
    children,
    hasBottomPadding,
    hasDivider = false,
    isOpen,
    onToggle,
}: { hasBottomPadding?: boolean; hasDivider?: boolean } & (
    | {
          children: React.ReactNode
          isOpen: boolean
          onToggle: (isOpen: boolean) => void
      }
    | { children: React.ReactNode; isOpen?: undefined; onToggle?: undefined }
)) => (
    <FactsTableRow
        hasBottomPadding={hasBottomPadding}
        hasDivider={hasDivider}
        label={
            <>
                Villkor <br />
                {onToggle && (
                    <Button onClick={() => onToggle(!isOpen)} sx={{ padding: 0 }}>
                        {isOpen ? 'Dölj' : 'Visa'} TR-räknare
                    </Button>
                )}
            </>
        }
    >
        {children}
    </FactsTableRow>
)

const RightsCalculator = ({
    currency,
    rightsRequired,
    unitSubPrice,
    unitSubPriceSek,
    unitsReceived,
}: {
    currency: NonNullable<IssueEntity['currency']>
    rightsRequired: NonNullable<IssueEntity['rightsRequired']>
    unitSubPrice: NonNullable<IssueEntity['unitSubPrice']>
    unitSubPriceSek: NonNullable<IssueEntity['unitSubPriceSek']>
    unitsReceived: NonNullable<IssueEntity['unitsReceived']>
}) => {
    const { i18n } = useTranslation()
    const userMeContext = useContext(UserMeContext)
    const { settings } = userMeContext.user || { settings: null }
    const locale = i18n.language
    const [amountValue, setAmountValue] = React.useState(settings?.rightsCalculator.amount ?? 100000)
    const [percentageValue, setPercentageValue] = React.useState(settings?.rightsCalculator.percentage ?? 3)

    const rightsCount = Math.ceil(((amountValue / +unitSubPriceSek) * rightsRequired) / unitsReceived)
    const sekAmount = (((percentageValue / 100) * +unitSubPrice) / rightsRequired) * unitsReceived

    const debouncedUpdateMe = useMemo(
        () =>
            debounce(async (field: string, value: number) => {
                await userMeContext.updateMe({
                    settings: {
                        ...settings,
                        rightsCalculator: {
                            amount: null,
                            percentage: null,
                            ...settings?.rightsCalculator,
                            [field]: value,
                        },
                    },
                })
            }, 500),
        [userMeContext, settings],
    )

    const handleChangeSetting = (
        field: keyof UserMe['settings']['rightsCalculator'],
        value: number,
        setState: Dispatch<SetStateAction<number>>,
    ) => {
        setState(value)

        void debouncedUpdateMe(field, value)
    }

    const numericFieldProps: Partial<PlainNumericFieldProps> = {
        sx: {
            marginTop: '-1px',
        },
        slotProps: {
            htmlInput: {
                sx: {
                    padding: 0,
                },
            },
        },
        variant: 'standard',
    }

    return (
        <>
            <FactsTableRow
                hasBottomPadding={false}
                hasDivider={false}
                label={
                    <>
                        Teckna{' '}
                        <PlainNumericField
                            {...numericFieldProps}
                            autoFocus={!settings?.rightsCalculator.amount}
                            onChange={(val) => handleChangeSetting('amount', +val, setAmountValue)}
                            sx={{
                                ...numericFieldProps.sx,
                                width:
                                    amountValue < 100000
                                        ? 45
                                        : amountValue < 1000000
                                          ? 55
                                          : amountValue < 1000000
                                            ? 65
                                            : 75,
                                '& input': {
                                    textAlign: 'center',
                                },
                            }}
                            value={amountValue.toString()}
                        />{' '}
                        SEK
                    </>
                }
            >
                {'=>'} {formatNumber(rightsCount, { decimals: 0, locale }) ?? '-'} rätter
            </FactsTableRow>

            <FactsTableRow
                label={
                    <>
                        <div style={{ display: 'inline-flex' }}>
                            <div style={{ paddingRight: 5 }}>Betala premie </div>
                            <PlainNumericField
                                {...numericFieldProps}
                                sx={{
                                    ...numericFieldProps.sx,
                                    width: 35,
                                    '& input': {
                                        textAlign: 'center',
                                    },
                                }}
                                percentage
                                onChange={(val) => handleChangeSetting('percentage', +val, setPercentageValue)}
                                value={percentageValue.toString()}
                            />
                        </div>
                        <DynamicIconButton
                            title={
                                <>
                                    Beräkna rätternas prisnivå så att de motsvarar det angivna påslaget på
                                    teckningskursen
                                </>
                            }
                            IconComponent={InfoIcon}
                            placement='right'
                            padding='0 0 0 10px'
                        />
                    </>
                }
            >
                {'=>'} {formatMoney(sekAmount, { currencySymbol: currency, locale, minDecimals: 2, maxDecimals: 5 })} /
                rätt
            </FactsTableRow>
        </>
    )
}

export function TermsRow({ issue }: { issue: Partial<IssueEntity> }) {
    const [isOpen, setIsOpen] = React.useState(false)
    const { i18n } = useTranslation()
    const locale = i18n.language

    const {
        currency,
        sharesRequired,
        rightsReceived,
        rightsRequired,
        unitsReceived,
        sharesInUnit,
        unitSubPrice,
        unitSubPriceSek,
    } = issue

    const notDetermined = !sharesRequired || !rightsReceived || !rightsRequired || !unitsReceived

    if (notDetermined) {
        return (
            <Wrapper>
                <span>Ej fastställt ännu</span>
            </Wrapper>
        )
    }

    const handleToggle = (_isOpen: boolean) => {
        setIsOpen(_isOpen)
    }

    const { valueOne: newShares, valueTwo: existingShares } = simplifyFraction(
        (sharesInUnit || 0) * unitsReceived * rightsReceived,
        sharesRequired * rightsRequired,
    )

    const termsExplanation = `${newShares} ${newShares > 1 ? 'nya aktier' : 'ny aktie'} på ${existingShares} ${
        existingShares > 1 ? 'befintliga' : 'befintlig'
    } (${newShares}:${existingShares})`
    const hasRightsCalculator =
        issue.type === 'Rights issue' &&
        typeof unitSubPrice === 'string' &&
        typeof unitSubPriceSek === 'string' &&
        typeof currency === 'string'

    return (
        <>
            <Wrapper hasBottomPadding={!hasRightsCalculator} hasDivider={!hasRightsCalculator}>
                {sharesInUnit !== 0 ? <Typography>{termsExplanation}</Typography> : null}
                {sharesOrUnits.sharesQuantityLabel(sharesRequired, locale)} ={'>'}{' '}
                {sharesOrUnits.rightsQuantityLabel(rightsReceived, locale)} <br />
                {sharesOrUnits.rightsQuantityLabel(rightsRequired, locale)} ={'>'}{' '}
                {sharesOrUnits.quantityLabel(issue, unitsReceived, locale)}
            </Wrapper>
            {hasRightsCalculator && (
                <>
                    <FactsTableRow
                        hasBottomPadding={!isOpen}
                        hasDivider={!isOpen}
                        label={
                            <>
                                <Button onClick={() => handleToggle(!isOpen)} sx={{ padding: 0 }}>
                                    {isOpen ? 'Dölj' : 'Visa'} TR-räknare
                                </Button>
                            </>
                        }
                    ></FactsTableRow>
                    {isOpen && (
                        <RightsCalculator
                            currency={currency}
                            rightsRequired={rightsRequired}
                            unitSubPrice={unitSubPrice}
                            unitSubPriceSek={unitSubPriceSek}
                            unitsReceived={unitsReceived}
                        />
                    )}
                </>
            )}
        </>
    )
}
